import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDate } from '@pk/shared';
import { useHouse, useWorld } from '../api/hooks';
import { Btn, ErrorBox, Loading, Panel, Screen, ScreenTop, ScrollArea, Section, Segmented, Stat, Empty } from '../components/ui';
import { IconAlert, IconBook, IconCheck, IconChevron, IconCrown, IconDagger, IconHourglass, IconPlus, IconQuill, IconScroll, IconShield, IconUsers, IconWar, ResPrestige, ResourceIcon } from '../components/icons';
import { ResourceChips, formatAmount, formatBalance } from '../components/game';
import { Heraldry } from '../components/Heraldry';
import { Affairs } from '../components/Affairs';
import { Ambition } from '../components/Ambition';
import { api } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { houseActions } from '../lib/houseActions';
import type { ActionIcon, HouseAction } from '../lib/houseActions';
import { FamilyScreen } from './Family';
import '../styles/home.css';

const VISIBLE = 3;
/** About three rows (56px + 6px gap) of the Needs-you list stay visible; the rest scrolls inside it. */
const TODO_H = 3 * 56 + 2 * 6 + 4;

export function HouseScreen() {
  const { t } = useTranslation();
  const house = useHouse();
  const world = useWorld();
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [moreRes, setMoreRes] = useState(false);
  const [params, setParams] = useSearchParams();
  const isFamily = params.get('view') === 'family';
  const setView = (v: 'overview' | 'family') => setParams(v === 'overview' ? {} : { view: v }, { replace: true });

  if (house.isLoading || world.isLoading) return <Loading />;
  if (house.isError || world.isError) return <ErrorBox error={house.error ?? world.error} onRetry={() => { void house.refetch(); void world.refetch(); }} />;
  const h = house.data!;
  const w = world.data!;
  const living = h.members.filter((m) => m.alive);
  const important = h.chronicle.filter((e) => e.important).slice(0, 3);
  const stage = h.inactivity.stage;
  const todo = houseActions(h, w);
  const hidden = todo.length - VISIBLE;

  return (
    <Screen className={`dash home${isFamily ? ' home-family' : ''}`}>
      <ScreenTop>
      {stage !== 'active' && (
        <div className="home-banner" role="status">
          <span className="attn-ico"><IconAlert /></span>
          <div className="attn-text"><b>{t(`inactivity.${stage}.title`)}</b><span>{t(`inactivity.${stage}.text`, { days: Math.floor(h.inactivity.realDaysInactive) })}</span></div>
          <Btn small onClick={() => { void api.heartbeat().then(() => qc.invalidateQueries({ queryKey: ['house'] })); }}>{t('inactivity.resume')}</Btn>
        </div>
      )}

      <div className="hero home-hero">
        <Heraldry seed={h.house.heraldrySeed} size={56} />
        <div className="grow">
          <h1>{t('house.title', { name: h.house.name })}</h1>
          <div className="hero-meta">
            <Stat icon={<ResPrestige />} label={t('house.prestige')} value={Math.floor(h.house.prestige)} />
            <Stat icon={<IconUsers />} label={t('house.familyShort')} value={t('house.members', { count: living.length })} />
          </div>
        </div>
      </div>

      <div className="home-subnav">
        <Segmented<'overview' | 'family'> value={params.get('view') === 'family' ? 'family' : 'overview'} onChange={setView} options={[
          { value: 'overview', label: t('nav.overview') },
          { value: 'family', label: t('nav.family') },
        ]} />
      </div>
      {isFamily ? null : (
        <Section title={t('house.needsYou')} className="home-needs" right={todo.length > 0 ? <span className="home-count num">{todo.length}</span> : undefined}>
          {todo.length === 0 ? (
            <div className="todo-empty"><IconCheck /><div><b>{t('house.todo.emptyTitle')}</b><span>{t('house.todo.emptyText')}</span></div></div>
          ) : (
            <>
              <ScrollArea className="todo-scroll" maxHeight={showAll ? 'min(320px, 40dvh)' : TODO_H} label={t('house.needsYou')}>
                <div className="todo-list">{todo.map((a) => <TodoRow key={a.key} a={a} />)}</div>
              </ScrollArea>
              {hidden > 0 && (
                <button type="button" className="todo-more" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
                  {showAll ? t('house.showLess') : t('house.seeAll', { count: todo.length })}
                </button>
              )}
            </>
          )}
        </Section>
      )}
      </ScreenTop>

      {isFamily ? <FamilyScreen /> : (
      <ScrollArea>
      <div className="dash-cols">
        <div>
          <Ambition h={h} day={w.day} />
          <Affairs h={h} w={w} />
        </div>

        <div>
          <Section title={t('house.treasury')}>
            <div className="treasury">
              {(['gold', 'food', 'manpower'] as const).map((k) => {
                const b = h.monthlyBalance[k];
                return (
                  <div key={k} className="tr-cell" title={t(`resource.${k}`)}>
                    <ResourceIcon k={k} />
                    <span className="tr-val num">{formatAmount(h.house.resources[k])}</span>
                    <span className={`tr-bal num ${b > 0.05 ? 'pos' : b < -0.05 ? 'neg' : ''}`}>{formatBalance(b)}{t('house.perMonthShort')}</span>
                  </div>
                );
              })}
              <button type="button" className="tr-more" aria-expanded={moreRes} onClick={() => setMoreRes((v) => !v)}>
                {moreRes ? t('house.less') : t('house.more')}<IconChevron className={moreRes ? 'open' : ''} />
              </button>
            </div>
            {moreRes && <div className="tr-all"><ResourceChips resources={h.house.resources} balance={h.monthlyBalance} /></div>}
          </Section>

          <Section title={t('house.recent')} right={<Link className="link small" to="/chronicle">{t('house.fullChronicle')}</Link>}>
            <Panel className="home-events">
              {important.length === 0 ? <Empty>{t('house.quiet')}</Empty> : important.map((e) => (
                <div key={e.id} className="chron important">
                  <span className="when">{formatDate(e.day)}</span>
                  <span className="what">{e.text}</span>
                </div>
              ))}
            </Panel>
          </Section>
        </div>
      </div>
      </ScrollArea>
      )}
    </Screen>
  );
}

const ICONS: Record<ActionIcon, ReactNode> = {
  perk: <IconBook />, proposal: <IconScroll />, letter: <IconQuill />, war: <IconWar />, family: <IconUsers />, child: <IconHourglass />,
  knight: <IconShield />, petition: <IconCrown />, build: <IconPlus />, army: <IconDagger />, plot: <IconDagger />,
};

/** One compact row: icon, one line, chevron. The longer hint is only a tooltip. */
function TodoRow({ a }: { a: HouseAction }) {
  const { t } = useTranslation();
  return (
    <Link to={a.to} className={`todo ${a.tone}`} title={t(a.hint.key, a.hint.params)}>
      <span className="todo-ico" aria-hidden="true">{ICONS[a.icon]}</span>
      <span className="todo-text"><b>{t(a.label.key, a.label.params)}</b></span>
      <IconChevron className="todo-chev" />
    </Link>
  );
}
